// @ts-check

import useLatestCallback from "@minohealth/common/hooks/useLatestCallback"
import { useSelector } from "react-redux"
import { StreamEndedEventDataSchema } from "./api"
import z from "zod"

const REVERT_MESSAGE_EVENT = "message:revert"
const OPEN_REPORT_PANEL_EVENT = "report-panel:open"
const OPEN_REPORT_FORM_EVENT = "report-form:open"

const UNSENT_FILE_ID = "UnsentId(FILE)"

const AgentMode = /**@type{const}*/({
  DEFAULT: "default",
  AGENT: "agent",
  WEB_SEARCH: "web_search",
  DEEP_RESEARCH: "deep_research",
  MOREMI_CO_RESEARCHER: "moremi_co_researcher",
  CANVAS_AGENT: "canvas_agent",
});
/** @typedef {typeof AgentMode[keyof typeof AgentMode]} AgentModeValue */

/** Modes whose agent can read and edit the document the user has open. */
const DOCUMENT_MODES = new Set([AgentMode.AGENT, AgentMode.MOREMI_CO_RESEARCHER, AgentMode.CANVAS_AGENT])

/**
 * The mode a chat starts in, and the mode the input goes back to when the user
 * clears a mode. A guest cannot use Agent, so a guest gets Search.
 * @param {boolean} isLoggedIn
 * @returns {AgentModeValue}
 */
function baseModeFor(isLoggedIn) {
  return isLoggedIn ? AgentMode.AGENT : AgentMode.WEB_SEARCH
}

const AI_USER = "AI"
const AGENT_USER = "agent"
const DEEP_RESEARCH_USER = "deepresearch"

/**
 * The mode a chat opens in, or null to keep the current mode. The latest reply
 * decides, so a Reviewer chat reopens in Reviewer mode and other chats leave it.
 * @param {Array<{ user?: string, meta?: { mode?: string } }>} messages
 * @param {AgentModeValue} currentMode
 * @returns {AgentModeValue | null}
 */
function modeForOpenedChat(messages, currentMode) {
  const latestReply = messages.findLast(({ user }) => (
    user === AI_USER || user === AGENT_USER || user === DEEP_RESEARCH_USER
  ))
  if (latestReply == null) return null

  const inReviewerMode = currentMode === AgentMode.CANVAS_AGENT
  if (latestReply.meta?.mode === AgentMode.CANVAS_AGENT) {
    return inReviewerMode ? null : AgentMode.CANVAS_AGENT
  }
  return inReviewerMode ? AgentMode.AGENT : null
}

/**
 * True when one of these messages holds a finished `create_canvas` tool call.
 * @param {Array<{ message?: unknown }>} messages
 */
function createdDocument(messages) {
  return messages.some(({ message }) => Array.isArray(message) && message.some(part => (
    part?.event === "ToolCallCompleted" && part.data?.tool_name === "create_canvas"
  )))
}

const STARTING_MESSAGE_EDIT_EVENT = "chat-bubble:starting-message-edit"
/**
 * @param {unknown} event 
 * @returns {event is StartingMessageEditEvent}
 */
function isStartingMessageEditEvent(event) {
  return event instanceof CustomEvent && event.type === STARTING_MESSAGE_EDIT_EVENT
}
/** @typedef {CustomEvent<{ messageId: string }>} StartingMessageEditEvent */

function useGetLatestUserToken() {
  const { user } = useSelector(state => state.app.datastate)
  return useLatestCallback(() => user?.userToken)
}

/**
 * Executes a callback with the resolved value of a promise, and returns the original promise if it is not a promise.
 * 
 * @template TResult
 * @template {(result: Awaited<TResult>) => any} TCallback
 * @param {TResult} result 
 * @param {TCallback} callback 
 * @returns {TResult | Promise<Awaited<TResult>>}
 */
function withResolved(result, callback) {
  if(result instanceof Promise) {
    return result.then(res => {
      const callbackResult = callback(res)
      if (callbackResult instanceof Promise) {
        return callbackResult.then(() => result)
      } else return result
    })
  }

  const callbackResult = callback(/**@type {Awaited<TResult>}*/(result))
  if(callbackResult instanceof Promise) {
    return callbackResult.then(() => /**@type {Awaited<TResult>}*/(result))
  } else return result
}

// ! Currently very primitive, does not handle cases with promise based callbacks
/**
 * @template {unknown} TData
 * @template {Error} TError
 * @template {unknown} TVariables
 * @template {unknown} TOnMutateResult
 * 
 * @param {Omit<import("@tanstack/react-query").UseMutationOptions<TData, TError, TVariables, TOnMutateResult>, 'mutationKey'>} mutationOptions 
 * @param {Omit<import("@tanstack/react-query").UseMutationOptions<TData, TError, TVariables, TOnMutateResult>, 'mutationKey' | 'onMutate'> & {onMutate?: (variables: TVariables, context: import("@tanstack/react-query").MutationFunctionContext, mutationResult: Awaited<TOnMutateResult>) => void}} extraOptions
 */
function extendMutationOptions(mutationOptions, extraOptions) {
  /** @type {Pick<import("@tanstack/react-query").UseMutationOptions<TData, TError, TVariables, TOnMutateResult>, 'onMutate' | 'onSuccess' | 'onError' | 'onSettled'>} */
  let mutationCallbacks = {}
  if(mutationOptions.onMutate ?? extraOptions.onMutate) {
    mutationCallbacks.onMutate = (...args) => {
      const result = mutationOptions.onMutate?.(...args)
      return withResolved(result, res => extraOptions.onMutate?.(...args, res))
    }
  }
  if(mutationOptions.onSuccess ?? extraOptions.onSuccess) {
    mutationCallbacks.onSuccess = (...args) => {
      const result = mutationOptions.onSuccess?.(...args)
      return withResolved(result, () => extraOptions.onSuccess?.(...args))
    }
  }
  if(mutationOptions.onError ?? extraOptions.onError) {
    mutationCallbacks.onError = (...args) => {
      const result = mutationOptions.onError?.(...args)
      return withResolved(result, () => extraOptions.onError?.(...args))
    }
  }
  if(mutationOptions.onSettled ?? extraOptions.onSettled) {
    mutationCallbacks.onSettled = (...args) => {
      const result = mutationOptions.onSettled?.(...args)
      return withResolved(result, () => extraOptions.onSettled?.(...args))
    }
  }

  const extraOptionsWithoutCallbacks = Object.fromEntries(
    Object.entries(extraOptions).filter(([key]) => (
      key !== 'onMutate' && key !== 'onSuccess' && key !== 'onError' && key !== 'onSettled'
    ))
  )

  return {
    ...mutationOptions,
    ...extraOptionsWithoutCallbacks,
    ...mutationCallbacks,
  }
}

export {
  REVERT_MESSAGE_EVENT,
  OPEN_REPORT_PANEL_EVENT,
  OPEN_REPORT_FORM_EVENT,
  UNSENT_FILE_ID,
  AgentMode,
  AI_USER,
  AGENT_USER,
  DEEP_RESEARCH_USER,
  STARTING_MESSAGE_EDIT_EVENT,
  isStartingMessageEditEvent,
  modeForOpenedChat,
  createdDocument,
  DOCUMENT_MODES,
  baseModeFor,

  useGetLatestUserToken,
  extendMutationOptions,
}